import { Typography, notification } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import * as clonedeep from 'lodash.clonedeep';
import React, { useState, useEffect, ReactText } from 'react';

import GachaWinningUnapprovedTable from '@/GachaWinning/components/GachaWinningUnapprovedTable';
import { GachaWinning } from '@/api-client/generated/models';
import { useGacha } from '@/hooks/context/GachaContext';
import { useGiftCodeCrypto } from '@/hooks/context/GiftCodeCryptContext';
import { createApiClientWithToken } from '@/service/PreRegistrationApiFactory';

const ListGachaWinningUnapproved: React.FC<{}> = () => {
  const api = createApiClientWithToken();
  const [gachaWinnings, setGachaWinnings] = useState<GachaWinning[]>([]);
  const [gachaWinningsNum, setGachaWinningsNum] = useState<number>(0);
  const [os, setOs] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(100); // 設定する必要があるかわからんので 100 でいったん固定（サーバー側も）
  const [selectedRowKeys, setSelectedRowKeys] = useState<ReactText[]>([]);
  const [searchWord, setSearchWord] = useState<string>('');
  const { gacha } = useGacha();
  const { giftCodeCrypto } = useGiftCodeCrypto();

  const listGachaWinning = async (
    status: string,
    _perPage: number,
    offset: number,
    _os: string,
    isSmsAuthedUser: boolean,
    _searchWord: string
  ) =>
    api.listGachaWinning(gacha.id, {
      limit: _perPage,
      offset,
      status,
      os: _os,
      isSmsAuthedUser,
      searchWord: _searchWord,
    });

  const decryptGiftCodes = (_gachaWinnings: GachaWinning[]) =>
    _gachaWinnings.map((gachaWinning) => {
      const newGachaWinning: GachaWinning = clonedeep(gachaWinning);
      newGachaWinning.giftCode = giftCodeCrypto.decryptGiftCode(newGachaWinning.giftCode);
      return newGachaWinning;
    });

  useEffect(() => {
    listGachaWinning('unapproved', perPage, 0, os, true, searchWord).then((response) => {
      setGachaWinnings(decryptGiftCodes(response.winnings || []));
      setGachaWinningsNum(response.totalNum);
    });
  }, []);

  const onChangeCurrentPage = async (newPage: number) => {
    const response = await listGachaWinning('unapproved', perPage, (newPage - 1) * perPage, os, true, searchWord);
    setCurrentPage(newPage);
    setGachaWinnings(decryptGiftCodes(response.winnings || []));
  };

  const onChangeOs = async (_os: string) => {
    const response = await listGachaWinning('unapproved', perPage, 0, _os, true, searchWord);

    setOs(_os);
    setCurrentPage(1);
    setGachaWinnings(decryptGiftCodes(response.winnings || []));
    setGachaWinningsNum(response.totalNum);
  };

  const onSearch = async (word: string) => {
    setSearchWord(word);
    const response = await listGachaWinning('unapproved', perPage, 0, os, true, word);

    setGachaWinnings(response.winnings);
    setCurrentPage(1);
    setGachaWinningsNum(response.totalNum);
  };

  const onApprove = async (approved: GachaWinning) => {
    await api.approveGachaWinning(gacha.id, {
      body: {
        winnings: gachaWinnings,
      },
    });

    notification.success({
      message: '当選者にギフトコードを送信しました',
    });
    setGachaWinnings(gachaWinnings.filter((gachaWinning) => gachaWinning.id !== approved.id));
  };

  const onReject = async (rejected: GachaWinning) => {
    await api.updateGachaWinningStatus(gacha.id, rejected.id, {
      body: {
        status: 'rejected',
      },
    });

    notification.success({
      message: 'ギフトコードの発行を拒否しました',
    });
    setGachaWinnings(gachaWinnings.filter((gachaWinning) => gachaWinning.id !== rejected.id));
  };

  const rowSelection: TableRowSelection<GachaWinning> = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
    selections: [
      {
        key: 'approve',
        text: '承認する',
        onSelect: async () => {
          const res = await api.approveGachaWinning(gacha.id, {
            body: {
              winnings: gachaWinnings.filter((winning) => selectedRowKeys.includes(winning.id)),
            },
          });

          setGachaWinnings(
            gachaWinnings.filter(
              (gachaWinning) =>
                !res.approvedWinnings.map((approvedWinning) => approvedWinning.id).includes(gachaWinning.id)
            )
          );

          if (!!res.failedWinnings && res.failedWinnings.length > 0) {
            notification.error({
              message: '承認に失敗したユーザが存在します。',
              description: res.failedWinnings
                .map(
                  (failedWinning) => `${(failedWinning.user || {}).nickname}: ${(failedWinning.user || {}).mailAddress}`
                )
                .join('\n'),
            });
          } else {
            notification.success({
              message: '選択したユーザを承認しました',
            });
          }
        },
      },
    ],
  };

  return (
    <div>
      <Typography.Title>未承認当選者一覧</Typography.Title>
      <GachaWinningUnapprovedTable
        gachaWinnings={gachaWinnings}
        gachaWinningsNum={gachaWinningsNum}
        currentPage={currentPage}
        perPage={perPage}
        rowSelection={rowSelection}
        onChangeCurrentPage={onChangeCurrentPage}
        onChangeOs={onChangeOs}
        onApprove={onApprove}
        onReject={onReject}
        onSearch={onSearch}
      />
    </div>
  );
};

export default ListGachaWinningUnapproved;
